<script setup lang="ts">
import { onMounted, ref, watch } from "vue";
import Icon from "./Icon.vue";
import StatusChip from "./StatusChip.vue";
import { api } from "../api/client";
import { locale, t } from "../i18n";

/**
 * What other systems know about the contact (an order in the shop, an invoice, a contract),
 * pushed by an integration through the API. Read-only here; shown only when there is a card.
 */
const props = defineProps<{ slug: string; contactId: string }>();

interface Card {
  key: string;
  source: string;
  title: string;
  status: string | null;
  tone: "neutral" | "success" | "warning" | "danger" | "info";
  fields: Array<{ label: string; value: string }>;
  link: { url: string; label: string | null } | null;
  updatedAt: string;
}
const cards = ref<Card[]>([]);
/** The chip colours the status chips already have, by the card's tone. */
const TONE_CHIP: Record<Card["tone"], string> = {
  success: "success",
  warning: "waiting_contact",
  danger: "failed",
  info: "active",
  neutral: "snoozed",
};

async function load() {
  try {
    cards.value = (
      await api<{ cards: Card[] }>(
        `/v1/c/${props.slug}/contacts/${props.contactId}/context-cards`,
      )
    ).cards;
  } catch {
    cards.value = [];
  }
}
onMounted(load);
watch(() => props.contactId, load);
defineExpose({ load });

const updated = (iso: string) =>
  new Date(iso).toLocaleString(locale.value, {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
</script>
<template>
  <section v-for="c in cards" :key="c.key" class="detail-section context-card">
    <h3 class="contact-section-title">
      {{ t("context.from", { source: c.source }) }}
    </h3>
    <div class="context-card-box">
      <div class="context-card-head">
        <strong class="grow">{{ c.title }}</strong>
        <StatusChip
          v-if="c.status"
          :status="TONE_CHIP[c.tone]"
          :label="c.status"
        />
      </div>
      <dl v-if="c.fields.length" class="context-card-fields">
        <div v-for="f in c.fields" :key="f.label">
          <dt>{{ f.label }}</dt>
          <dd>{{ f.value }}</dd>
        </div>
      </dl>
      <a
        v-if="c.link"
        :href="c.link.url"
        target="_blank"
        rel="noopener"
        class="context-card-link"
        ><Icon name="external" :size="16" />{{
          c.link.label || t("context.open")
        }}</a
      >
      <small class="muted">{{
        t("context.updated", { when: updated(c.updatedAt) })
      }}</small>
    </div>
  </section>
</template>
